// eslint-disable-next-line max-classes-per-file
import { fixShortMonth, getWeekNumber } from '@/utils/common';

// eslint-disable-next-line max-classes-per-file
class GanttDayScale {
  constructor(firstDate, lastDate) {
    this.startDate = new Date(firstDate.getFullYear(), firstDate.getMonth(), 1);
    this.finishDate = new Date(lastDate.getFullYear(), lastDate.getMonth() + 1, 0);
  }

  getHolidays() {
    const holidays = [];
    const lastIndex = this.getCell(this.finishDate);

    for (let i = 0; i <= lastIndex; i += 1) {
      const date = this.countDate(this.startDate, i);
      if ([0, 6].includes(date.getDay())) {
        holidays.push({ i, date });
      }
    }

    return holidays;
  }

  getCell(date) {
    // Discard the time and time-zone information.
    const utc1 = Date.UTC(this.startDate.getFullYear(), this.startDate.getMonth(), this.startDate.getDate()); // eslint-disable-line max-len
    const utc2 = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());

    const MS_PER_DAY = 1000 * 60 * 60 * 24;
    return Math.floor((utc2 - utc1) / MS_PER_DAY);
  }

  // eslint-disable-next-line
  countDate(date, delta) {
    return new Date(new Date(date).setDate(date.getDate() + delta));
  }

  createHeader(cellWidth) {
    const days = [];
    const months = [];

    const todayDate = new Date();
    todayDate.setHours(0);
    todayDate.setMinutes(0);
    todayDate.setSeconds(0);
    todayDate.setMilliseconds(0);
    const todayTime = todayDate.getTime();

    let curr = null;
    let x = 0;

    const refDate = new Date(this.startDate);

    while (refDate.getTime() <= this.finishDate.getTime()) {
      const day = refDate.getDate();

      if (day === 1) {
        const lastDateOfMonth = new Date(refDate.getFullYear(), refDate.getMonth() + 1, 0);

        months.push({
          x: x * cellWidth,
          w: lastDateOfMonth.getDate() * cellWidth,
          title: lastDateOfMonth.toLocaleDateString('ru', { year: 'numeric', month: 'long' }),
        });
      }

      const dayObj = { x: x * cellWidth, w: cellWidth, title: day };
      if (todayTime === refDate.getTime()) {
        curr = dayObj;
      }

      days.push(dayObj);
      refDate.setDate(day + 1);
      x += 1;
    }

    return { top: months, bottom: days, curr };
  }
}

// eslint-disable-next-line max-classes-per-file
class GanttMonthScale {
  constructor(firstDate, lastDate) {
    this.startDate = new Date(firstDate.getFullYear(), 0, 1);
    this.finishDate = new Date(lastDate.getFullYear(), 12, 0);
  }

  // eslint-disable-next-line class-methods-use-this
  getHolidays() {
    return [];
  }

  getCell(date) {
    let diff = (date.getFullYear() - this.startDate.getFullYear()) * 12;
    diff += date.getMonth() - this.startDate.getMonth();

    return diff;
  }

  // eslint-disable-next-line
  countDate(date, delta) {
    return new Date(new Date(date).setMonth(date.getMonth() + delta));
  }

  createHeader(cellWidth) {
    const days = [];
    const months = [];
    let x = 0;

    const refDate = new Date(this.startDate);

    while (refDate.getTime() <= this.finishDate.getTime()) {
      const month = refDate.getMonth();

      if (month === 0) {
        months.push({
          x: x * cellWidth,
          w: 12 * cellWidth,
          title: refDate.toLocaleDateString('ru', { year: 'numeric' }),
        });
      }

      days.push({
        x: x * cellWidth,
        w: cellWidth,
        title: fixShortMonth(refDate.toLocaleDateString('ru', { month: 'short' })),
      });
      refDate.setMonth(month + 1);
      x += 1;
    }

    // todo add curr key (see GanttWeekScale.createHeader)
    return { top: months, bottom: days };
  }
}

// eslint-disable-next-line max-classes-per-file
class GanttWeekScale {
  constructor(firstDate, lastDate) {
    this.finishDate = new Date(lastDate.getFullYear(), 12, 0);
    this.startDate = new Date(firstDate.getFullYear(), 0, 1);
    this.startDate.setDate(this.startDate.getDate() + 1 - (this.startDate.getDay() || 7));
  }

  // eslint-disable-next-line class-methods-use-this
  getHolidays() {
    return [];
  }

  getCell(date) {
    // Discard the time and time-zone information.
    const utc1 = Date.UTC(this.startDate.getFullYear(), this.startDate.getMonth(), this.startDate.getDate()); // eslint-disable-line max-len
    const utc2 = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());

    const MS_PER_DAY_WEEK = 1000 * 60 * 60 * 24 * 7;
    return Math.floor((utc2 - utc1) / MS_PER_DAY_WEEK);
  }

  // eslint-disable-next-line
  countDate(date, delta) {
    return new Date(new Date(date).setDate(date.getDate() + delta * 7));
  }

  createHeader(cellWidth) {
    const months = [];
    const weeks = [];
    let x = 0;

    const todayYear = new Date().getFullYear();
    const todayWeekNumber = getWeekNumber(new Date());
    let curr = null;

    let refDate = new Date(this.startDate);
    while (refDate.getTime() <= this.finishDate.getTime()) {
      const weekNumber = getWeekNumber(refDate);
      const week = {
        x: x * cellWidth,
        w: cellWidth,
        title: weekNumber,
      };

      if (weekNumber === todayWeekNumber && todayYear === refDate.getFullYear()) {
        curr = week;
      }

      weeks.push(week);
      refDate.setDate(refDate.getDate() + 7);
      x += 1;
    }

    refDate = new Date(this.startDate);
    refDate.setDate(1);
    while (refDate.getTime() <= this.finishDate.getTime()) {
      const diff = (refDate - this.startDate) / (1000 * 60 * 60 * 24 * 7);
      const lastDateOfMonth = new Date(refDate.getFullYear(), refDate.getMonth() + 1, 0);
      months.push({
        x: diff * cellWidth,
        w: (lastDateOfMonth.getDate() / 7) * cellWidth,
        title: lastDateOfMonth.toLocaleDateString('ru', { year: 'numeric', month: 'long' }),
      });
      refDate.setMonth(refDate.getMonth() + 1);
    }

    return { top: months, bottom: weeks, curr };
  }
}

export default class GanttScaleFactory {
  // eslint-disable-next-line class-methods-use-this
  create(scale, firstDate, lastDate) {
    switch (scale) {
      case 'day': return new GanttDayScale(firstDate, lastDate);
      case 'week': return new GanttWeekScale(firstDate, lastDate);
      case 'month': return new GanttMonthScale(firstDate, lastDate);
      default: throw new Error(`Unknown scale format ${scale}`);
    }
  }
}
